import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import store2 from 'store2';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet } from '../../store/fetch';
import { codeToColorCode } from '../../utils/conditionHelper';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { EditPanelFilterMultiSelect, InputSearchText, ISelectOption, SmallFilterSelect } from '../elements';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { DisplayMessage } from '../utils/displayMessage';
import { transformLabelsToSelectOptions } from '../utils/entityLabels.utils';
import { Bounds, GenericMap, IMarker } from '../utils/GenericMap';
import { getConditionLabelForMap } from '../utils/getConditionLabelForMap';

interface IObjectsMapProps extends ITranslateProps, IRouterProps {
    languageCode: string;
    useBatches: boolean;
    isPdokMapLayerAvailable: boolean;
    isDroneNoFlyMapNlAvailable: boolean;
    shouldClusterMarkers: boolean;
    isClient: boolean;
    userRole: Roles;
}

interface IObject {
    id: string;
    name: string;
    code: string;
    location: string;
    latitude: number;
    longitude: number;
    surveyCondition?: undefined | string;
    surveyInspectionStandardType?: undefined | string;
    shapeGeoJson?: undefined | string;
    objectTypeName: string;
}

interface IValueText {
    value: string;
    text: string;
}

interface IObjectsMapState {
    loading: boolean;
    searching: boolean;
    noDataAvailable: boolean;
    items: IObject[];
    latitude: number;
    longitude: number;
    currentIndex: number;
    currentTitle: string;
    currentId: string;
    objectTypes: IValueText[];
    batches: IValueText[];
    ownerCompanies: IValueText[];
    objectComplexLocations: IValueText[];
    objectTypeId?: undefined | string;
    batchId?: undefined | string;
    companyId?: undefined | string;
    inspectionStandardType?: undefined | string;
    objectCode?: undefined | string;
    locationCode?: undefined | string;
    knownLabels: ISelectOption[];
    selectedObjectLabels: string[];
    mapBounds?: undefined | Bounds;
    isCenterMapNl: boolean;
    markersToFit?: undefined | IMarker[];
    isFirstSearch: boolean;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        useBatches: authentication.useBatches,
        languageCode: language.code,
        isPdokMapLayerAvailable: authentication.isPdokMapLayerAvailable,
        isDroneNoFlyMapNlAvailable: authentication.isDroneNoFlyMapNlAvailable,
        shouldClusterMarkers: authentication.shouldClusterMarkers,
        userRole: authentication.role,
        isClient: authentication.isClient,
    };
}

const storeKey = 'ObjectsMap';

class ObjectsMap extends Component<IObjectsMapProps, IObjectsMapState> {
    constructor(props: IObjectsMapProps) {
        super(props);

        const isCenterMapNl = isCustomerVersion('rws') || isCustomerVersion('zxy');

        this.state = {
            loading: true,
            searching: true,
            noDataAvailable: false,
            items: [],
            latitude: 51.808504,
            longitude: 5.703935,
            currentIndex: 0,
            currentTitle: '',
            currentId: '',
            objectTypes: [],
            batches: [],
            ownerCompanies: [],
            objectComplexLocations: [],
            knownLabels: [],
            selectedObjectLabels: [],
            isCenterMapNl,
            isFirstSearch: true,
        };
    }

    public override async componentDidMount() {
        if (store2.get(storeKey)) {
            if (isCustomerVersion('rijksvastgoed')) {
                await this.setState({ objectCode: store2.get(storeKey).objectCode, locationCode: store2.get(storeKey).locationCode });
            } else {
                if (store2.get(storeKey) !== 'undefined') {
                    await this.setState({ objectTypeId: store2.get(storeKey) });
                }
            }
        }

        await this.loadObjectTypes();
        await this.search();
        await this.loadOwnerCompanies();
        await this.loadKnownLabels();

        if (this.props.useBatches) {
            await this.loadBatches();
        }
        if (isCustomerVersion('rijksvastgoed')) {
            await this.loadObjectComplexLocations();
        }
    }

    public override async componentDidUpdate(prevProps: IObjectsMapProps, prevState: IObjectsMapState) {
        if (this.props.languageCode !== prevProps.languageCode) {
            await this.loadObjectTypes();
            await this.search();
        }
        if (
            this.state.batchId !== prevState.batchId ||
            this.state.locationCode !== prevState.locationCode ||
            this.state.companyId !== prevState.companyId
        ) {
            await this.search();
        } else if (this.state.inspectionStandardType !== prevState.inspectionStandardType) {
            await this.loadBatches();
            await this.search();
        } else if (this.state.objectTypeId !== prevState.objectTypeId) {
            await this.search();
        }
    }

    public override render() {
        const isAdminOrClientSurveyorOperator =
            (this.props.isClient && (this.props.userRole === Roles.Operator || this.props.userRole === Roles.Surveyor)) ||
            this.props.userRole === Roles.Administrator;

        return (
            <span>
                <header id="topbar" className="ph10">
                    <div className="topbar-left">
                        <ol className="breadcrumb">
                            <li className="crumb-active">
                                <span className="fa fa-building pr10"></span>
                                <Link to="/objects">{this.props.t('common.objects')}</Link>
                            </li>
                        </ol>
                    </div>
                </header>

                <section id="content" className="pn posr">
                    <div style={{ position: 'relative', height: '100%' }}>
                        <form method="post" action="/" id="filter">
                            <div className="map-menu admin-form mw250">
                                {isCustomerVersion('rijksvastgoed') && (
                                    <div className="mt10">
                                        <SmallFilterSelect
                                            name="locationCode"
                                            value={this.state.locationCode}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('common.location')}
                                            placeholder={this.props.t('common.findItem', this.props.t('common.location'))}
                                            options={this.state.objectComplexLocations}
                                        />
                                    </div>
                                )}
                                <div className="mt10">
                                    <SmallFilterSelect
                                        name="objectTypeId"
                                        value={this.state.objectTypeId}
                                        handleChange={async (value) => {
                                            await this.setState({ searching: true });
                                            await this.handleChange(value, 'objectTypeId');
                                        }}
                                        topLabel={this.props.t('common.objectType')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.objectTypes'))}
                                        options={this.state.objectTypes}
                                    />
                                </div>
                                <div className="mt10">
                                    <SmallFilterSelect
                                        name="inspectionStandardType"
                                        value={this.state.inspectionStandardType}
                                        handleChange={this.handleChange.bind(this)}
                                        topLabel={this.props.t('inspectionStandard.inspectionTypes')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.type'))}
                                        options={getInspectionStandardTypeOptions(this.props.t)}
                                    />
                                </div>

                                {this.props.useBatches && (
                                    <div className="mt10">
                                        <SmallFilterSelect
                                            name="batchId"
                                            value={this.state.batchId}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel="Batch"
                                            placeholder={this.props.t('common.allItems', 'Batches')}
                                            options={this.state.batches}
                                        />
                                    </div>
                                )}
                                {isAdminOrClientSurveyorOperator && (
                                    <div className="mt10">
                                        <SmallFilterSelect
                                            name="companyId"
                                            value={this.state.companyId}
                                            handleChange={this.handleChange.bind(this)}
                                            topLabel={this.props.t('common.owner')}
                                            placeholder={this.props.t('common.allItems', this.props.t('common.owners'))}
                                            options={this.state.ownerCompanies}
                                        />
                                    </div>
                                )}

                                <div className="mt10">
                                    <EditPanelFilterMultiSelect
                                        name="selectedObjectLabels"
                                        topLabel={this.props.t('object.filterObjectsByObjectLabel')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.labels'))}
                                        value={this.state.selectedObjectLabels}
                                        options={this.state.knownLabels}
                                        handleChange={this.handleObjectLabelChange.bind(this)}
                                    />
                                </div>

                                <div className="mt20">
                                    <InputSearchText
                                        name="objectCode"
                                        value={this.state.objectCode}
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={async () => this.search()}
                                        topLabel={this.props.t('object.codeObjectComplex')}
                                    />
                                </div>
                            </div>
                        </form>

                        {(this.state.loading || this.state.searching) && !this.state.noDataAvailable && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <Spinner />
                            </div>
                        )}

                        {this.state.noDataAvailable && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <DisplayMessage message={this.props.t('object.noItemsOnMap')} messageType="noData" />
                            </div>
                        )}

                        {this.state.items && (
                            <GenericMap
                                mapLoaded={this.mapLoaded.bind(this)}
                                zoom={11}
                                center={{ lat: this.state.latitude, lng: this.state.longitude }}
                                isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                                isDroneNoFlyMapNlAvailable={this.props.isDroneNoFlyMapNlAvailable}
                                markers={this.state.items.map((object) => ({
                                    key: object.id,
                                    position: { lat: object.latitude, lng: object.longitude },
                                    title: object.name,
                                    onClick: this.handleMarkerRightClick.bind(this, object),
                                    color: codeToColorCode(object.surveyCondition, object.surveyInspectionStandardType),
                                    shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
                                    conditionLabel: getConditionLabelForMap(object.surveyCondition, this.state.inspectionStandardType),
                                    condition: object.surveyCondition,
                                    isTerrain: object.objectTypeName.toLowerCase() === 'terrein' || object.objectTypeName.toLowerCase() === 'terrain',
                                }))}
                                shouldClusterMarkers={this.props.shouldClusterMarkers}
                                onBoundsChanged={async (bounds) => {
                                    await this.setState({ mapBounds: bounds });
                                    await this.search.bind(this)(true);
                                }}
                                onZoomChanged={() => {
                                    void this.search.bind(this)(true);
                                }}
                                markersToFit={this.state.markersToFit}
                            />
                        )}
                    </div>
                </section>
            </span>
        );
    }

    private async loadObjectTypes() {
        const result = await fetchGet('/api/objectTypesAvailable', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ objectTypes: valueTextArray });
        }
    }

    private async loadOwnerCompanies() {
        const result = await fetchGet('/api/companyList?type=owner', {}, ['NO_DATA_AVAILABLE']);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ ownerCompanies: valueTextArray });
        }
    }

    private async loadObjectComplexLocations() {
        const result = await fetchGet('/api/objectsComplexList', {});
        await this.setState({ objectComplexLocations: result.json?.items });
    }

    private async loadBatches() {
        const queryParamters = {} as { [key: string]: string };
        if (this.state.inspectionStandardType) {
            queryParamters.inspectionStandardType = this.state.inspectionStandardType;
        }
        const result = await fetchGet('/api/batcheslist', queryParamters, [], [403]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ batches: valueTextArray });
        } else {
            await this.setState({ batches: [] });
        }
    }

    private async mapLoaded() {
        await this.setState({ loading: false });
    }

    private handleMarkerRightClick(object: IObject) {
        // If no object type is selected then terrain is not clickable. Prevents wrong clicks.
        if (!this.state.objectTypeId && object.objectTypeName.toLowerCase() !== 'terrein' && object.objectTypeName.toLowerCase() !== 'terrain') {
            this.props.navigate(`/objects/${object.id}`);
        } else if (this.state.objectTypeId) {
            this.props.navigate(`/objects/${object.id}`);
        }
    }

    private async handleChange(value: any, stateKey: string) {
        if (stateKey === 'batchId') {
            await this.setState({ batchId: value, objectTypeId: undefined });
        } else {
            const newState = {} as any;
            newState[stateKey] = value;
            await this.setState(newState);
        }
    }

    private async search(isKeepMapPosition = false) {
        if (!this.state.mapBounds) {
            await this.setState({ items: [] });
            return;
        }
        if (this.state.isFirstSearch) {
            isKeepMapPosition = false;
            await this.setState({ isFirstSearch: false });
        }

        const url = '/api/objectmarkers';

        await this.setState({ searching: true, noDataAvailable: false });
        const result = await fetchGet(url, {
            objectTypeId: this.state.objectTypeId ? this.state.objectTypeId : undefined,
            batchId: this.state.batchId ? this.state.batchId : undefined,
            companyId: this.state.companyId ? this.state.companyId : undefined,
            objectCode: this.state.objectCode ? this.state.objectCode : undefined,
            locationCode: this.state.locationCode ? this.state.locationCode : undefined,
            surveyInspectionStandardType: this.state.inspectionStandardType ? this.state.inspectionStandardType : undefined,
            labels: this.state.selectedObjectLabels,
            boundingBoxXMin: this.state.mapBounds.xMin,
            boundingBoxXMax: this.state.mapBounds.xMax,
            boundingBoxYMin: this.state.mapBounds.yMin,
            boundingBoxYMax: this.state.mapBounds.yMax,
            limit: this.props.shouldClusterMarkers ? 5000 : 2000,
        });

        if (isCustomerVersion('rijksvastgoed')) {
            store2.set(storeKey, { locationCode: this.state.locationCode, objectCode: this.state.objectCode });
        } else {
            store2.set(storeKey, this.state.objectTypeId);
        }

        if (result.json.items.length === 0) {
            await this.setState({ noDataAvailable: true, items: [], markersToFit: [] });
        } else {
            const { hasItemsInBoundingBox } = result.json;
            if (hasItemsInBoundingBox) {
                await this.setState({ items: result.json.items, markersToFit: [] });
            } else {
                if (!isKeepMapPosition) {
                    const markersToFit = result.json.items.map((object: IObject) => ({
                        key: object.id,
                        position: { lat: object.latitude, lng: object.longitude },
                        shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
                    }));
                    await this.setState({ items: result.json.items, markersToFit });
                }
            }
        }
        await this.setState({ searching: false });
    }

    private async loadKnownLabels() {
        const url = '/api/objects/knownLabels';
        const result = await fetchGet(url);
        const knownLabels = transformLabelsToSelectOptions(result?.json);
        await this.setState({ knownLabels });
    }

    private async handleObjectLabelChange(value: string[]) {
        await this.handleChange(value, 'selectedObjectLabels');
        await this.search();
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps)(ObjectsMap));
